import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useAppDispatch } from '../../store/hooks';
import { loginSuccess } from '../../store/slices/authslice';
import axios from 'axios';
import { endpoints } from '../../api/api';
import { useTermsConsent } from '../../hooks/useTermsConsent';
import { trackLoginStart, trackLoginSuccess, trackLoginFailure, trackOtpSent, trackOtpVerification, trackSignUp, identifyUser } from '../../utils/analytics';
import MobileNumberForm from './components/MobileNumberForm';
import OtpVerificationForm from './components/OtpVerificationForm';
import TermsModal from './components/TermsModal';

// Google signup flow phases
type GoogleSignupPhase = 'phone' | 'otp';

interface GoogleProfileData {
  email: string;
  firstName: string;
  lastName: string;
}

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // OTP login state
  const [mobileNumber, setMobileNumber] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Google signup state
  const [googleSignupPhase, setGoogleSignupPhase] = useState<GoogleSignupPhase | null>(null);
  const [googleProfileData, setGoogleProfileData] = useState<GoogleProfileData | null>(null);

  const [pendingNav, setPendingNav] = useState<string | null>(null);

  const { showTerms, triggerTerms, handleAccept: handleTermsAccept, handleDecline: handleTermsDecline } = useTermsConsent({
    onAccept: () => navigate(pendingNav || '/'),
    onDecline: () => {
      setPendingNav(null);
      setOtpSent(false);
      setOtp(['', '', '', '', '', '']);
      setGoogleSignupPhase(null);
      setGoogleProfileData(null);
      setError(null);
    },
  });

  const dispatchLoginSuccess = (user: any) => {
    dispatch(loginSuccess({
      id: user.id,
      name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User',
      mobile: user.phoneNumber || '',
      email: user.email || '',
      emailVerified: user.emailVerified || false,
    }));
    // email/name intentionally excluded — see identifyUser call in App.tsx.
    identifyUser(user.id, {
      email_verified: user.emailVerified,
    });
  };

  // ─── Google OAuth ──────────────────────────────────────────────────────────

  const handleGoogleSuccess = async (accessToken: string) => {
    setGoogleLoading(true);
    setError(null);
    trackLoginStart('google');
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch user profile');
      const profile = await response.json();

      if (!profile.email) {
        setError('Unable to fetch email from Google. Please try manual login.');
        trackLoginFailure('google', 'Unable to fetch email from Google');
        return;
      }

      const backendResponse = await axios.post(endpoints.googleLogin, {
        email: profile.email,
        firstName: profile.given_name || '',
        lastName: profile.family_name || '',
      });

      // Existing user — log them in
      const { accessToken: jwt, refreshToken, user } = backendResponse.data;
      localStorage.setItem('accessToken', jwt);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      dispatchLoginSuccess(user);
      trackLoginSuccess('google');
      if (user.termsStatus !== 'accepted') { setPendingNav('/'); triggerTerms(); }
      else { navigate('/'); }
    } catch (err: any) {
      // Interceptor rejects with err.response directly, so check both shapes
      const errData = err?.data || err?.response?.data;
      if (errData?.userNotFound) {
        // New user — start Google signup phone verification
        const { email, firstName, lastName } = errData.prefillData;
        setGoogleProfileData({ email, firstName, lastName });
        setGoogleSignupPhase('phone');
        setMobileNumber('');
        setOtp(['', '', '', '', '', '']);
        setError(null);
      } else {
        trackLoginFailure('google', err.message || 'Unknown error');
        setError('Failed to sign in with Google. Please try again.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => handleGoogleSuccess(tokenResponse.access_token),
    onError: () => setError('Failed to sign in with Google.'),
    scope: 'openid email profile',
  });

  // ─── OTP helpers (shared by normal login & Google signup) ─────────────────

  const sendOtp = (phone: string) => {
    const phoneNumber = '+91' + phone;
    axios.post(endpoints.sendOTP, { phoneNumber })
      .then((res) => {
        setSessionId(res.data.sessionId);
        trackOtpSent();
        if (googleSignupPhase !== null) {
          setGoogleSignupPhase('otp');
        } else {
          setOtpSent(true);
        }
      })
      .catch((err) => {
        const reason = err?.response?.data?.message || err.message || 'Failed to send OTP.';
        trackLoginFailure(googleSignupPhase !== null ? 'google' : 'otp', reason);
        setError(reason);
      })
      .finally(() => setIsLoading(false));
  };

  // ─── Normal OTP login ──────────────────────────────────────────────────────

  const handleMobileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mobileNumber.length !== 10) { setError('Please enter a valid 10-digit mobile number'); return; }
    setIsLoading(true);
    setError(null);
    trackLoginStart('otp');
    sendOtp(mobileNumber);
  };

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) { setError('Please enter complete 6-digit OTP'); return; }
    setIsLoading(true);
    setError(null);

    const phoneNumber = '+91' + mobileNumber;
    axios.post(endpoints.verifyOTP, { sessionId, otp: otpString, phoneNumber })
      .then((response) => {
        const { accessToken, refreshToken, user, isNewUser } = response.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(user));
        dispatchLoginSuccess(user);
        trackOtpVerification('success');
        trackLoginSuccess('otp');
        if (isNewUser) { trackSignUp(); navigate('/signup-info'); }
        else if (user.termsStatus !== 'accepted') { setPendingNav('/'); triggerTerms(); }
        else { navigate('/'); }
      })
      .catch((err) => {
        trackOtpVerification('failed');
        trackLoginFailure('otp', err?.response?.data?.message || err.message || 'Invalid OTP');
        setError(err?.response?.data?.message || err.message || 'Invalid OTP. Please try again.');
      })
      .finally(() => setIsLoading(false));
  };

  const handleResendOtp = () => {
    setIsLoading(true);
    setError(null);
    setOtp(['', '', '', '', '', '']);
    sendOtp(mobileNumber);
  };

  // ─── Google signup phone step ──────────────────────────────────────────────

  const handleGoogleSignupPhoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mobileNumber.length !== 10) { setError('Please enter a valid 10-digit mobile number'); return; }
    setIsLoading(true);
    setError(null);
    sendOtp(mobileNumber);
  };

  const handleGoogleSignupOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) { setError('Please enter complete 6-digit OTP'); return; }
    if (!googleProfileData) return;

    setIsLoading(true);
    setError(null);

    const phoneNumber = '+91' + mobileNumber;
    axios.post(endpoints.googleSignup, {
      email: googleProfileData.email,
      firstName: googleProfileData.firstName,
      lastName: googleProfileData.lastName,
      phoneNumber,
      sessionId,
      otp: otpString,
    })
      .then((response) => {
        const { accessToken, refreshToken, user } = response.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(user));
        dispatchLoginSuccess(user);
        trackOtpVerification('success');
        trackSignUp();
        trackLoginSuccess('google');
        setPendingNav('/');
        triggerTerms();
      })
      .catch((err) => {
        trackOtpVerification('failed');
        const msg = err?.data?.message || err?.response?.data?.message || err.message || 'Failed to create account. Please try again.';
        setError(msg);
      })
      .finally(() => setIsLoading(false));
  };

  const handleGoogleSignupResendOtp = () => {
    setIsLoading(true);
    setError(null);
    setOtp(['', '', '', '', '', '']);
    sendOtp(mobileNumber);
  };

  const handleCancelGoogleSignup = () => {
    setGoogleSignupPhase(null);
    setGoogleProfileData(null);
    setMobileNumber('');
    setOtp(['', '', '', '', '', '']);
    setError(null);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const steps = [
    { n: '01', title: 'Write or upload', desc: 'Built-in editor or bring your own manuscript' },
    { n: '02', title: 'Format & bind', desc: 'Trim size, paper, binding, lamination' },
    { n: '03', title: 'Design your cover', desc: 'Upload yours or have our team craft one' },
    { n: '04', title: 'Get published', desc: 'We handle printing, ISBN & delivery' },
  ];

  return (
    <>
    {showTerms && (
      <TermsModal onAccept={handleTermsAccept} onDecline={handleTermsDecline} />
    )}
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-6 py-12"
      style={{ background: 'radial-gradient(ellipse 80% 60% at 15% 40%, #fff7ed 0%, #fef3e2 30%, #ffffff 70%)' }}>

      {/* Checkered grid — fades from edges inward via radial mask */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(180,120,60,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(180,120,60,0.07) 1px, transparent 1px)',
        backgroundSize: '44px 44px',
        WebkitMaskImage: 'radial-gradient(ellipse 85% 80% at 50% 50%, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
        maskImage: 'radial-gradient(ellipse 85% 80% at 50% 50%, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
      }} />

      {/* Soft accent blob — top left */}
      <div className="absolute top-[-60px] left-[-60px] w-[360px] h-[360px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, #fed7aa 0%, transparent 65%)', opacity: 0.45 }} />
      {/* Soft accent blob — bottom right */}
      <div className="absolute bottom-[-80px] right-[-40px] w-[280px] h-[280px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, #ffedd5 0%, transparent 65%)', opacity: 0.5 }} />

      {/* Artistic arc lines — top left corner */}
      <svg className="absolute top-0 left-0 pointer-events-none" width="520" height="520" viewBox="0 0 520 520" fill="none">
        {[70, 110, 150, 195, 242, 292, 345, 400, 458, 520].map((r, i) => (
          <path
            key={r}
            d={`M 0 ${r} A ${r} ${r} 0 0 1 ${r} 0`}
            stroke="#f97316"
            strokeWidth="0.7"
            strokeOpacity={0.055 + i * 0.006}
          />
        ))}
      </svg>

      {/* Artistic arc lines — bottom right corner (mirrored) */}
      <svg className="absolute bottom-0 right-0 pointer-events-none" width="360" height="360" viewBox="0 0 360 360" fill="none"
        style={{ transform: 'rotate(180deg)' }}>
        {[60, 100, 145, 195, 250, 310, 370].map((r, i) => (
          <path
            key={r}
            d={`M 0 ${r} A ${r} ${r} 0 0 1 ${r} 0`}
            stroke="#f97316"
            strokeWidth="0.7"
            strokeOpacity={0.04 + i * 0.005}
          />
        ))}
      </svg>

      {/* ── Main content: two sections on one canvas ── */}
      <div className="relative z-10 w-full max-w-5xl flex items-center gap-16 lg:gap-24">

        {/* ── Left: Brand content ── */}
        <div className="hidden lg:flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-12">
            <img src="/logo.png" alt="IndiePress" className="w-9 h-9" />
            <span className="text-gray-900 text-base font-semibold">IndiePress</span>
          </div>

          <p className="text-orange-500 text-[11px] font-semibold tracking-[0.18em] uppercase mb-3">DIY Publishing Platform</p>

          {/* Headline with decorative underline + sparkle */}
          <div className="relative mb-4">
            <h1 className="text-gray-900 leading-[1.12]" style={{ fontFamily: "'Bell MT', serif", fontSize: '3rem' }}>
              Bring your story<br />to life with<br />IndiePress
            </h1>

            {/* Curved underline under "IndiePress" */}
            <svg className="mt-1" width="260" height="18" viewBox="0 0 260 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M 4 12 C 40 4, 90 2, 140 7 C 180 11, 220 6, 256 10"
                stroke="#f97316"
                strokeWidth="2.2"
                strokeLinecap="round"
                fill="none"
                opacity="0.75"
              />
            </svg>

          </div>

          <p className="text-gray-500 text-[15px] mb-12 leading-relaxed max-w-xs">
            From manuscript to printed copy — all in one place, without a traditional publisher.
          </p>

          <div className="space-y-0">
            {steps.map((s, i) => (
              <div key={s.n} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-[9px] font-bold">{s.n}</span>
                  </div>
                  {i < steps.length - 1 && <div className="w-px bg-orange-200 my-1" style={{ minHeight: '24px' }} />}
                </div>
                <div className="pb-5">
                  <p className="text-gray-800 text-sm font-semibold leading-none mb-1">{s.title}</p>
                  <p className="text-gray-400 text-xs leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-gray-300 text-xs mt-12">
            Nasadiya Technologies Pvt. Ltd.
          </p>
        </div>

        {/* ── Right: Login form — frosted glass, no hard card edge ── */}
        <div className="w-full lg:w-[380px] flex-shrink-0">
          <div className="bg-white/50 backdrop-blur-md rounded-2xl px-8 py-8"
            style={{ boxShadow: '0 4px 40px rgba(251,146,60,0.08), 0 1px 3px rgba(0,0,0,0.04)' }}>
            {/* Logo — mobile only */}
            <div className="flex items-center mb-8 lg:hidden">
              <img src="/logo.png" alt="IndiePress" className="w-8 h-8 mr-3" />
              <span className="text-gray-900 text-xl font-semibold">IndiePress</span>
            </div>

          {/* ── Google Signup: phone step ── */}
          {googleSignupPhase === 'phone' && googleProfileData && (
            <>
              <h2 className="text-gray-900 text-xl font-semibold mb-1">One more step</h2>
              <p className="text-gray-500 text-sm mb-5">Verify your phone number to complete sign up</p>

              <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-6">
                <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold text-sm flex-shrink-0">
                  {googleProfileData.firstName?.[0] || googleProfileData.email[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-gray-900 text-sm font-medium truncate">
                    {googleProfileData.firstName} {googleProfileData.lastName}
                  </p>
                  <p className="text-gray-400 text-xs truncate">{googleProfileData.email}</p>
                </div>
              </div>

              <form onSubmit={handleGoogleSignupPhoneSubmit}>
                <label className="block text-gray-700 text-sm font-medium mb-2">Mobile Number</label>
                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden mb-4 focus-within:border-orange-500 focus-within:ring-1 focus-within:ring-orange-500">
                  <span className="px-3 py-3 bg-gray-50 text-gray-600 text-sm border-r border-gray-300 select-none">+91</span>
                  <input
                    type="tel"
                    value={mobileNumber}
                    onChange={(e) => { setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10)); setError(null); }}
                    className="flex-1 px-3 py-3 text-gray-900 text-sm outline-none bg-white"
                    placeholder="Enter your mobile number"
                    maxLength={10}
                    autoFocus
                  />
                </div>
                {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
                <button
                  type="submit"
                  disabled={isLoading || mobileNumber.length !== 10}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3 rounded-lg font-semibold text-sm transition-colors mb-3"
                >
                  {isLoading ? 'Sending OTP...' : 'Send OTP'}
                </button>
                <button type="button" onClick={handleCancelGoogleSignup} className="w-full text-gray-500 text-sm hover:text-gray-700 py-2">
                  ← Back to login
                </button>
              </form>
            </>
          )}

          {/* ── Google Signup: OTP step ── */}
          {googleSignupPhase === 'otp' && googleProfileData && (
            <>
              <h2 className="text-gray-900 text-xl font-semibold mb-1">Verify OTP</h2>
              <p className="text-gray-500 text-sm mb-6">
                Enter the 6-digit OTP sent to <span className="font-medium text-gray-700">+91 {mobileNumber}</span>
              </p>
              <OtpVerificationForm
                mobileNumber={mobileNumber}
                otp={otp}
                isLoading={isLoading}
                error={error}
                onOtpChange={(index, value) => { const n = [...otp]; n[index] = value; setOtp(n); }}
                onKeyDown={() => {}}
                onSubmit={handleGoogleSignupOtpSubmit}
                onResend={handleGoogleSignupResendOtp}
                onEditMobile={() => { setGoogleSignupPhase('phone'); setOtp(['', '', '', '', '', '']); setError(null); }}
              />
            </>
          )}

          {/* ── Normal login ── */}
          {googleSignupPhase === null && (
            <>
              <h2 className="text-gray-900 text-xl font-semibold mb-1">Welcome back</h2>
              <p className="text-gray-400 text-sm mb-8">Sign in to continue to IndiePress</p>
              {!otpSent ? (
                <MobileNumberForm
                  mobileNumber={mobileNumber}
                  isLoading={isLoading}
                  googleLoading={googleLoading}
                  error={error}
                  onMobileChange={setMobileNumber}
                  onSubmit={handleMobileSubmit}
                  onGoogleLogin={() => googleLogin()}
                />
              ) : (
                <OtpVerificationForm
                  mobileNumber={mobileNumber}
                  otp={otp}
                  isLoading={isLoading}
                  error={error}
                  onOtpChange={(index, value) => { const n = [...otp]; n[index] = value; setOtp(n); }}
                  onKeyDown={() => {}}
                  onSubmit={handleOtpSubmit}
                  onResend={handleResendOtp}
                  onEditMobile={() => { setOtpSent(false); setOtp(['', '', '', '', '', '']); setError(null); }}
                />
              )}
            </>
          )}
          </div>
        </div>
      </div>
    </div>
    </>
  );

};

export default Login;
